import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllServiceTableReportData,
  getServiceTableReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { ServiceTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "serviceName",
  "timesUsed",
  "totalRevenue",
  "totalDiscount",
  "totalRefund",
  "averageTimeSpent",
  "type",
  "category",
  "subcategory",
  "sacCode",
  "variantName",
  "variantNumber",
  "sellingPrice",
];
const filterByList = [
  "No. of Times Used",
  "Total Revenue",
  "Total Discount",
  "Total Refund",
  "Avg. Time Spent",
  "Type",
  "SAC Code",
  "Variant ID",
  "Selling Price",
];
const dropFields = ["Type"];
const splitRows = [
  "No. of Times Used",
  "Total Revenue",
  "Total Discount",
  "Total Refund",
  "Avg. Time Spent",
  "Selling Price",
];
const initSearch = {
  serviceName: "",
  category: [],
  subcategory: [],
  variantName: "",
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};

const ServiceTableReport = () => {
  const dispatch = useDispatch();
  const hasFetchedAllRef = useRef(false);
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { serviceTable, allServiceTable } = useSelector(
    (state) => state?.report,
  );
  const rowsPerPage = 10;

  useEffect(() => {
    onApiCall(1).then(() => {
      if (hasFetchedAllRef.current) return;
      hasFetchedAllRef.current = true;
      dispatch(getAllServiceTableReportData());
    });
  }, [
    searchValues?.serviceName,
    searchValues?.category,
    searchValues?.subcategory,
    searchValues?.variantName,
  ]);

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "summary",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (searchValues?.serviceName?.length > 0) {
      reqObj.serviceName = [searchValues?.serviceName];
    }
    if (searchValues?.category?.length > 0) {
      reqObj.category = searchValues?.category?.map((li) => li?.value);
    }
    if (searchValues?.subcategory?.length > 0) {
      reqObj.subcategory = searchValues?.subcategory?.map((li) => li?.value);
    }
    if (searchValues?.variantName?.length > 0) {
      reqObj.variantName = [searchValues?.variantName];
    }

    if (filterValues?.selectedList?.includes("Type")) {
      reqObj.serviceType = filterValues?.values?.["Type"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("SAC Code")) {
      reqObj.hsnSacCode = [filterValues?.values?.["SAC Code"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Variant ID")) {
      reqObj.variantNumber = [filterValues?.values?.["Variant ID"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("No. of Times Used")) {
      reqObj.minTimesUsed = Number(
        filterValues?.values?.["Min Times Used"] ?? 0,
      );
      reqObj.maxTimesUsed = Number(
        filterValues?.values?.["Max Times Used"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Total Revenue")) {
      reqObj.minRevenue = Number(filterValues?.values?.["Min Revenue"] ?? 0);
      reqObj.maxRevenue = Number(filterValues?.values?.["Max Revenue"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Total Discount")) {
      reqObj.minDiscount = Number(filterValues?.values?.["Min Discount"] ?? 0);
      reqObj.maxDiscount = Number(filterValues?.values?.["Max Discount"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Total Refund")) {
      reqObj.minRefund = Number(filterValues?.values?.["Min Refund"] ?? 0);
      reqObj.maxRefund = Number(filterValues?.values?.["Max Refund"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Avg. Time Spent")) {
      reqObj.minTimeSpent = Number(
        filterValues?.values?.["Min Time Spent"] ?? 0,
      );
      reqObj.maxTimeSpent = Number(
        filterValues?.values?.["Max Time Spent"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Selling Price")) {
      reqObj.minSellingPrice = Number(
        filterValues?.values?.["Min Selling Price"] ?? 0,
      );
      reqObj.maxSellingPrice = Number(
        filterValues?.values?.["Max Selling Price"] ?? 0,
      );
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    return dispatch(getServiceTableReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((serviceTable?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return (serviceTable?.data ?? [])?.map((li) => ({
      ...li,
      totalRevenue: Number(li?.revenue ?? 0)?.toFixed(2),
      totalDiscount: Number(li?.discount ?? 0)?.toFixed(2),
      totalRefund: Number(li?.totalRefund ?? 0)?.toFixed(2),
      sellingPrice: Number(li?.servicePrice)?.toFixed(2),
      sacCode: li?.hsnSacCode,
    }));
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const getServiceList = () => {
    if (!allServiceTable?.data) return [];
    const reqList = (allServiceTable?.data ?? [])
      ?.map((li) => li?.serviceName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getCategoryList = () => {
    if (!allServiceTable?.data) return [];
    const reqList = (allServiceTable?.data ?? [])
      ?.map((li) => li?.category)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getSubCategoryList = () => {
    if (!allServiceTable?.data) return [];
    const reqList = (allServiceTable?.data ?? [])
      ?.map((li) => li?.subcategory)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getVariantNames = () => {
    if (!allServiceTable?.data) return [];
    const reqList = (allServiceTable?.data ?? [])
      ?.map((li) => li?.variantName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return uniqList;
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const getFieldName = (name, type) => {
    if (name === "No. of Times Used") {
      if (type === "start") return "Min Times Used";
      return "Max Times Used";
    }
    if (name === "Total Revenue") {
      if (type === "start") return "Min Revenue";
      return "Max Revenue";
    }
    if (name === "Total Discount") {
      if (type === "start") return "Min Discount";
      return "Max Discount";
    }
    if (name === "Total Refund") {
      if (type === "start") return "Min Refund";
      return "Max Refund";
    }
    if (name === "Avg. Time Spent") {
      if (type === "start") return "Min Time Spent";
      return "Max Time Spent";
    }
    if (name === "Selling Price") {
      if (type === "start") return "Min Selling Price";
      return "Max Selling Price";
    }
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const getSACCodeList = () => {
    if (!allServiceTable?.data) return [];
    const reqList = (allServiceTable?.data ?? [])
      ?.map((li) => li?.hsnSacCode)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getVariantIds = () => {
    if (!allServiceTable?.data) return [];
    const reqList = (allServiceTable?.data ?? [])
      ?.map((li) => li?.variantNumber)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getSuggestionList = (typ) => {
    if (!typ) return [];
    if (typ === "SAC Code") return getSACCodeList();
    if (typ === "Variant ID") return getVariantIds();
  };

  return (
    <>
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getServiceList()}
              value={searchValues?.serviceName}
              name="serviceName"
              handleChange={onSearchChange}
              label="Service Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getCategoryList()}
              value={searchValues?.category ?? []}
              name="category"
              handleChange={onSearchChange}
              label="Category"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getSubCategoryList()}
              value={searchValues?.subcategory ?? []}
              name="subcategory"
              handleChange={onSearchChange}
              label="Subcategory"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getVariantNames()}
              value={searchValues?.variantName}
              name="variantName"
              handleChange={onSearchChange}
              label="Varinat Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {serviceTable?.totalRecords ?? 0}
      </Typography>
      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="56vh"
        />
        <div className="flex1-end mtMin30">
          {Math.ceil((serviceTable?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => {
                      return (
                        <div className="mt20">
                          {dropFields?.includes(li) ? (
                            <CustomSelect
                              list={ServiceTypes}
                              value={filterValues?.values?.[li] ?? []}
                              name={li}
                              handleChange={onFilterValuesChange}
                              label={li}
                              multiSelectSearchCheck
                            />
                          ) : splitRows?.includes(li) ? (
                            <div className="flex-row-between-align-center">
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "start")}
                                  fullWidth
                                  name={getFieldName(li, "start")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "start")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "start")}
                                  mobileDate={li === "Appt Date"}
                                  type="number"
                                />
                              </div>
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "end")}
                                  fullWidth
                                  name={getFieldName(li, "end")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "end")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "end")}
                                  mobileDate={li === "Appt Date"}
                                  type="number"
                                />
                              </div>
                            </div>
                          ) : (
                            <CustomTextField
                              placeholder={li}
                              fullWidth
                              name={li}
                              handleChange={onFilterValuesChange}
                              value={filterValues?.values?.[li] ?? ""}
                              label={li}
                              suggetions
                              options={getSuggestionList(li)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default ServiceTableReport;
